import React, { useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Slider from 'react-slick';
import { Container, Typography, Button, Box, useTheme, useMediaQuery, Paper, IconButton, Stack, Grid } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { AppData } from '../../data/appData';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './AppCarousel.scss';

interface AppCarouselProps {
    apps: AppData[];
}

const AppCarousel: React.FC<AppCarouselProps> = ({ apps }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isExtraSmall = useMediaQuery('(max-width:480px)');
    const sliderRef = useRef<Slider>(null);

    const settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5000,
        arrows: false,
        pauseOnHover: true,
        pauseOnFocus: true,
        pauseOnDotsHover: true,
        beforeChange: () => {
            if (sliderRef.current) {
                sliderRef.current.slickPlay();
            }
        }
    };

    const goToPrev = () => {
        if (sliderRef.current) {
            sliderRef.current.slickPrev();
        }
    };

    const goToNext = () => {
        if (sliderRef.current) {
            sliderRef.current.slickNext();
        }
    };

    // Add custom styles to the container for the dots
    const customSliderStyles = {
        '.slick-dots li.slick-active button:before': {
            color: theme.palette.primary.main
        },
        '.slick-dots': {
            bottom: isMobile ? '-30px' : '-40px'
        }
    };

    return (
        <div className="app-carousel">
            <Container maxWidth="lg">
                <Box mb={isMobile ? 2 : 4}>
                    <Typography
                        variant={isMobile ? "h5" : "h4"}
                        component="h2"
                        className="section-title"
                        sx={{
                            // '&:after': {
                            //     backgroundColor: theme.palette.primary.main
                            // },
                            fontWeight: 'bold',
                            textAlign: isMobile ? 'center' : 'left'
                        }}
                    >
                        {isMobile ? "Our Apps" : "Featured Applications"}
                    </Typography>
                </Box>

                <div className="carousel-container">
                    {!isMobile && (
                        <IconButton
                            onClick={goToPrev}
                            className="carousel-arrow prev"
                            aria-label="Previous slide"
                        >
                            <ArrowBackIosNewIcon />
                        </IconButton>
                    )}

                    <Box sx={customSliderStyles}>
                        <Slider ref={sliderRef} {...settings}>
                            {apps.map((app) => (
                                <div key={app.id} className="carousel-slide">
                                    <Paper
                                        elevation={3}
                                        className="carousel-content"
                                        sx={{
                                            ...(app.customHero?.imageUrl ? {
                                                backgroundImage: `url(${app.customHero.imageUrl})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                backgroundRepeat: 'no-repeat'
                                            } : {
                                                backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                                            }),
                                            height: isExtraSmall ? 'auto' : isMobile ? '550px' : '400px'
                                        }}
                                    >
                                        <Box
                                            className="carousel-text"
                                            sx={{
                                                textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                                padding: isMobile ? '1.5rem 1rem' : '2rem'
                                            }}
                                        >
                                            <Typography
                                                variant={isMobile ? "h4" : "h3"}
                                                component="h1"
                                                className="app-title"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    marginBottom: isMobile ? '0.75rem' : '1rem',
                                                    color: app.customHero?.titleColor ? app.customHero?.titleColor : theme.palette.primary.contrastText
                                                }}
                                            >
                                                {app.name}
                                            </Typography>
                                            <Typography
                                                variant={isMobile ? "body1" : "h6"}
                                                className="app-description"
                                                sx={{
                                                    marginBottom: isMobile ? '1rem' : '1.5rem',
                                                    opacity: 0.9,
                                                    color: app.customHero?.descColor ? app.customHero?.descColor : theme.palette.primary.contrastText
                                                }}
                                            >
                                                {app.description}
                                            </Typography>

                                            <Grid container spacing={2} sx={{ mt: 2 }}>
                                                {isMobile ? (
                                                    <>
                                                        <Grid item xs={12} sx={{ mb: 1 }}>
                                                            <Button
                                                                component={RouterLink}
                                                                to={`/app/${app.id}`}
                                                                variant="contained"
                                                                color="secondary"
                                                                size="medium"
                                                                sx={{ minWidth: '150px', whiteSpace: 'nowrap' }}
                                                            >
                                                                Learn More!
                                                            </Button>
                                                        </Grid>

                                                        <Grid item xs={12}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <Stack direction="row" spacing={1}>
                                                                    {app.appStoreLink && (
                                                                        <a
                                                                            href={app.appStoreLink}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="store-badge-link"
                                                                        >
                                                                            <img
                                                                                src="/images/app-store-badge.svg"
                                                                                alt="Download on the App Store"
                                                                                className="store-badge-small"
                                                                            />
                                                                        </a>
                                                                    )}

                                                                    {app.googlePlayLink && (
                                                                        <a
                                                                            href={app.googlePlayLink}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="store-badge-link"
                                                                        >
                                                                            <img
                                                                                src="/images/google-play-badge.svg"
                                                                                alt="Get it on Google Play"
                                                                                className="store-badge-small"
                                                                            />
                                                                        </a>
                                                                    )}
                                                                </Stack>
                                                            </Box>
                                                        </Grid>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Grid item xs={12} sm={6} md={6}>
                                                            <Button
                                                                component={RouterLink}
                                                                to={`/app/${app.id}`}
                                                                variant="contained"
                                                                color="secondary"
                                                                size="large"
                                                                sx={{ minWidth: '150px', whiteSpace: 'nowrap' }}
                                                            >
                                                                Learn More!
                                                            </Button>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={6}>
                                                            <Stack direction="row" spacing={1} justifyContent="flex-start">
                                                                {app.appStoreLink && (
                                                                    <a
                                                                        href={app.appStoreLink}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="store-badge-link"
                                                                    >
                                                                        <img
                                                                            src="/images/app-store-badge.svg"
                                                                            alt="Download on the App Store"
                                                                            className="store-badge-small"
                                                                        />
                                                                    </a>
                                                                )}

                                                                {app.googlePlayLink && (
                                                                    <a
                                                                        href={app.googlePlayLink}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="store-badge-link"
                                                                    >
                                                                        <img
                                                                            src="/images/google-play-badge.svg"
                                                                            alt="Get it on Google Play"
                                                                            className="store-badge-small"
                                                                        />
                                                                    </a>
                                                                )}
                                                            </Stack>
                                                        </Grid>
                                                    </>
                                                )}
                                            </Grid>
                                        </Box>
                                        <Box className="carousel-image">
                                            <img src={app.icon} alt={app.name} />
                                        </Box>
                                    </Paper>
                                </div>
                            ))}
                        </Slider>
                    </Box>

                    {!isMobile && (
                        <IconButton
                            onClick={goToNext}
                            className="carousel-arrow next"
                            aria-label="Next slide"
                        >
                            <ArrowForwardIosIcon />
                        </IconButton>
                    )}
                </div>
            </Container>
        </div>
    );
};

export default AppCarousel;